/**
 * Current Stock Table Component
 * @see US-12b.4 - Page Liste Stock
 * @see sprint-datagrid-migration Phase 1.3
 */

import { useMemo, useCallback } from 'react';
import {
  DataGrid,
  Badge,
  textColumn,
  dateColumn,
  dateTimeColumn,
  numberColumn,
  moneyColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { useCurrency } from '@/hooks';
import { formatQuantity } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  STOCK_STATUS_LABELS,
  STOCK_STATUS_COLORS,
  MarketTypeLabels,
} from '@bstock/shared';
import type { CurrentStockItem, QueryCurrentStockDto, StockStatus, MarketType } from '@bstock/shared';

type SortField = QueryCurrentStockDto['sortBy'];
type SortOrder = QueryCurrentStockDto['sortOrder'];

interface CurrentStockTableProps {
  items: CurrentStockItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  sortBy: SortField;
  sortOrder: SortOrder;
  onRowClick: (item: CurrentStockItem) => void;
  onSort: (field: SortField, order: SortOrder) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  showFinancials?: boolean;
}

/**
 * Map column ids to backend sort field names. The backend only accepts
 * a few sortable fields; non-mapped columns are auto non-sortable in
 * controlled mode.
 */
const SORT_FIELD_MAP: Record<string, SortField> = {
  sku: 'sku',
  name: 'name',
  lastInventoryDate: 'lastInventoryDate',
  currentStock: 'currentStock',
  lastMovementDate: 'lastMovementDate',
};

export function CurrentStockTable({
  items,
  pagination,
  isLoading,
  sortBy,
  sortOrder,
  onRowClick,
  onSort,
  onPageChange,
  onLimitChange,
  showFinancials = true,
}: CurrentStockTableProps) {
  const currency = useCurrency();

  const columns = useMemo<DataGridColumn<CurrentStockItem>[]>(() => {
    const cols: DataGridColumn<CurrentStockItem>[] = [
      textColumn<CurrentStockItem>({
        id: 'sku',
        header: 'SKU',
        accessor: (r) => r.rawProduct.sku,
        cellClassName: 'font-mono text-xs text-neutral-600',
      }),
      textColumn<CurrentStockItem>({
        id: 'name',
        header: 'Produit',
        accessor: (r) => r.rawProduct.name,
        ellipsis: true,
        maxWidth: '280px',
        cell: (r) => (
          <div>
            <div className="font-medium text-dark-500">{r.rawProduct.name}</div>
            <div className="text-xs text-neutral-400">
              {MarketTypeLabels[r.rawProduct.marketType as MarketType]}
            </div>
          </div>
        ),
      }),
      textColumn<CurrentStockItem>({
        id: 'category',
        header: 'Catégorie',
        sortable: false,
        accessor: (r) => r.rawProduct.category.name,
        cell: (r) => (
          <div className="flex items-center gap-2">
            {r.rawProduct.category.color && (
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: r.rawProduct.category.color }}
              />
            )}
            <span className="text-sm text-neutral-600">
              {r.rawProduct.category.name}
            </span>
          </div>
        ),
      }),
      dateTimeColumn<CurrentStockItem>({
        id: 'lastInventoryDate',
        header: 'Dernier inventaire',
        accessor: (r) =>
          r.lastInventoryDate ? new Date(r.lastInventoryDate) : null,
      }),
      numberColumn<CurrentStockItem>({
        id: 'currentStock',
        header: 'Stock actuel',
        accessor: 'currentStock',
        cell: (r) => {
          const isNegative = r.currentStock < 0;
          return (
            <div className="text-right">
              <span
                className={cn(
                  'font-semibold',
                  isNegative ? 'text-danger-600' : 'text-dark-500'
                )}
              >
                {formatQuantity(r.currentStock)}
              </span>
              <span className="text-xs text-neutral-400 ml-1">
                {r.rawProduct.recipeUnit.symbol}
              </span>
            </div>
          );
        },
      }),
      textColumn<CurrentStockItem>({
        id: 'stockStatus',
        header: 'Statut',
        align: 'center',
        sortable: false,
        exportValue: (r) => STOCK_STATUS_LABELS[r.stockStatus as StockStatus],
        cell: (r) => (
          <Badge variant={STOCK_STATUS_COLORS[r.stockStatus as StockStatus]}>
            {STOCK_STATUS_LABELS[r.stockStatus as StockStatus]}
          </Badge>
        ),
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<CurrentStockItem>({
          id: 'stockValue',
          header: 'Valeur',
          sortable: false,
          accessor: (r) => r.stockValue / 100,
          cellClassName: 'font-medium',
        }),
        moneyColumn<CurrentStockItem>({
          id: 'currentPmp',
          header: 'PMP',
          sortable: false,
          accessor: (r) => (r.currentPmp ? r.currentPmp / 100 : null),
        })
      );
    }

    cols.push(
      dateColumn<CurrentStockItem>({
        id: 'lastMovementDate',
        header: 'Dernier mvt',
        accessor: (r) =>
          r.lastMovementDate ? new Date(r.lastMovementDate) : null,
      })
    );

    return cols;
  }, [currency, showFinancials]);

  // ---- Sort (server-side, controlled) ------------------------------------
  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(
      ([, backendField]) => backendField === sortBy
    )?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!next) {
        onSort('name', 'asc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort]
  );

  return (
    <DataGrid<CurrentStockItem>
      data={items}
      columns={columns}
      rowKey={(r) => r.rawProductId}
      isLoading={isLoading}
      skeletonRows={10}
      onRowClick={onRowClick}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: { base: 'stock_actuel' },
        sheetName: 'Stock actuel',
        position: 'header',
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucun produit en stock
        </div>
      }
    />
  );
}
